import { Fragment, useEffect, useMemo, useState } from "react";
import { TrackToggle } from "../components/EventCard";
import { LiveBadge, SectionHeader, Spinner } from "../components/bits";
import { api, type EventWindow, type WindowInfo } from "../lib/api";
import { brandColor, windowState } from "../lib/events";
import { clockParts, countdown, relativeTime, shortDate, windowLabel } from "../lib/format";
import { Link } from "../lib/router";
import { useApp } from "../lib/store";

const DAY_MS = 86_400_000;
const HOUR_MS = 3_600_000;
/** Beyond this, an upcoming window reads "later": its clock dims and the
 *  relative time is suppressed so "in 2 weeks" never competes with "in 1 hour". */
const SOON_MS = 48 * HOUR_MS;
/** The countdown shifts to warn (amber) inside this window — felt, not parsed. */
const IMMINENT_MS = HOUR_MS;

type Verdict = "live" | "next" | "recent";
type RowState = "live" | "next" | "upcoming" | "later" | "past";

interface Annotated {
  w: EventWindow;
  begin: number;
  end: number;
  valid: boolean;
  state: ReturnType<typeof windowState>;
}

interface Band {
  key: string;
  begin: number;
  windows: Annotated[];
  allPast: boolean;
  hasLive: boolean;
}

interface Schedule {
  verdict: Annotated | null;
  verdictKind: Verdict | null;
  liveCount: number;
  nextUpId: string | null;
  bands: Band[];
  focusedKey: string;
}

/** One shared wall-clock for the page, ticking every second so the hero
 *  countdown feels alive and `upcoming → live` self-heals with no reload.
 *  Paused while the tab is hidden — no background work while the backend
 *  is busy parsing matches. Digits only ever swap as text (never animate). */
function useNow(): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    let id: ReturnType<typeof setInterval> | null = null;
    const tick = () => setNow(Date.now());
    const start = () => {
      if (id == null) id = setInterval(tick, 1000);
    };
    const stop = () => {
      if (id != null) {
        clearInterval(id);
        id = null;
      }
    };
    const onVisibility = () => {
      if (document.hidden) stop();
      else {
        tick();
        start();
      }
    };
    start();
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      stop();
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []);
  return now;
}

/** The single source of truth: which window is the answer, and how the rest
 *  group by day. The Verdict and the schedule's "shown above" marker both read
 *  from this one computed value, so they can never disagree. */
function buildSchedule(windows: EventWindow[], now: number): Schedule {
  const ann: Annotated[] = windows.map(w => {
    const begin = Date.parse(w.begin_time);
    const end = Date.parse(w.end_time);
    return { w, begin, end, valid: !Number.isNaN(begin), state: windowState(w, now) };
  });

  const live = ann.filter(a => a.valid && a.state === "live");
  const futures = ann.filter(a => a.valid && a.begin > now).sort((a, b) => a.begin - b.begin);
  const pasts = ann.filter(a => a.valid && a.state === "past");

  const nextUp = futures[0] ?? null;

  // Precedence: live (soonest-begun, then soonest-ending) → next-up → most
  // recently played. NaN-dated windows can never be elevated as the answer.
  let verdict: Annotated | null = null;
  let verdictKind: Verdict | null = null;
  if (live.length > 0) {
    verdict = [...live].sort((a, b) => a.begin - b.begin || a.end - b.end)[0]!;
    verdictKind = "live";
  } else if (nextUp) {
    verdict = nextUp;
    verdictKind = "next";
  } else if (pasts.length > 0) {
    verdict = [...pasts].sort((a, b) => b.end - a.end)[0]!;
    verdictKind = "recent";
  }

  const byDay = new Map<string, Annotated[]>();
  for (const a of ann) {
    const key = a.valid ? new Date(a.begin).toDateString() : "unknown";
    byDay.set(key, [...(byDay.get(key) ?? []), a]);
  }
  const bands: Band[] = [...byDay.entries()]
    .map(([key, ws]) => {
      const sorted = [...ws].sort((a, b) => a.begin - b.begin);
      const begins = sorted.map(a => a.begin).filter(b => !Number.isNaN(b));
      return {
        key,
        begin: key === "unknown" ? Number.MAX_SAFE_INTEGER : Math.min(...begins),
        windows: sorted,
        allPast: sorted.every(a => a.state === "past"),
        hasLive: sorted.some(a => a.state === "live"),
      };
    })
    .sort((a, b) => a.begin - b.begin);

  const focusedKey =
    verdict && verdict.valid ? new Date(verdict.begin).toDateString() : new Date(now).toDateString();

  return {
    verdict,
    verdictKind,
    liveCount: live.length,
    nextUpId: nextUp?.w.event_window_id ?? null,
    bands,
    focusedKey,
  };
}

function rowStateOf(a: Annotated, nextUpId: string | null, now: number): RowState {
  if (a.state === "live") return "live";
  if (a.w.event_window_id === nextUpId) return "next";
  if (a.valid && a.begin > now) return a.begin - now <= SOON_MS ? "upcoming" : "later";
  if (!a.valid) return "later";
  return "past";
}

function dayLabel(key: string, now: number): string {
  if (key === "unknown") return "Scheduled";
  if (key === new Date(now).toDateString()) return "Today";
  if (key === new Date(now + DAY_MS).toDateString()) return "Tomorrow";
  const d = new Date(key);
  return Number.isNaN(d.getTime()) ? key : d.toLocaleDateString(undefined, { weekday: "long" });
}

export function EventPage({ baseId }: { baseId: string }) {
  const { grouped, status, storedByEvent, region, setRegion, admin } = useApp();
  const now = useNow();

  const group = useMemo(() => grouped?.find(g => g.base_id === baseId) ?? null, [grouped, baseId]);
  const active = useMemo(() => {
    const rs = group?.regions.map(r => r.region) ?? [];
    return region && rs.includes(region) ? region : (rs[0] ?? "NAC");
  }, [group, region]);
  const regional = useMemo(
    () => group?.regions.find(r => r.region === active) ?? null,
    [group, active],
  );
  const windows = useMemo(() => regional?.windows ?? [], [regional]);
  const schedule = useMemo(() => buildSchedule(windows, now), [windows, now]);

  const watchedById = useMemo(
    () => new Map((status?.windows ?? []).map(w => [w.event_window_id, w] as const)),
    [status],
  );
  const storedByWindow = useMemo(() => {
    const m = new Map<string, number>();
    for (const s of storedByEvent?.get(baseId) ?? []) {
      m.set(s.event_window_id, (m.get(s.event_window_id) ?? 0) + 1);
    }
    return m;
  }, [storedByEvent, baseId]);

  if (!grouped) return <Spinner label="loading the event catalog" />;
  if (!group) {
    return (
      <main className="mx-auto max-w-6xl px-6 py-16 text-center">
        <p className="text-sm text-dim">This event is not in the catalog any more.</p>
        <Link to="/" className="mt-3 inline-block text-[13px] text-accent hover:text-accent-bright">
          ← back to tournaments
        </Link>
      </main>
    );
  }

  const regions = group.regions.map(r => r.region);
  const eventId = regional?.event_id ?? "";
  const isTracked = (status?.tracked ?? []).some(t => t.event_base === baseId);
  const color = brandColor(group.display, "#2e2e3e");
  const art = group.display?.tile_image;

  const storedCount = storedByEvent?.get(baseId)?.length ?? 0;
  const playedCount = windows.filter(w => windowState(w, now) === "past").length;
  const otherRegion = regions.find(r => r !== active && group.regions.some(g => g.region === r && g.windows.length > 0));

  const pills = [
    `${windows.length} ${windows.length === 1 ? "window" : "windows"}`,
    playedCount > 0 ? `${playedCount} played` : null,
    storedCount > 0 ? `${storedCount} captured` : null,
  ].filter((p): p is string => p !== null);

  return (
    <main className="mx-auto max-w-6xl px-6 py-8">
      <Link to="/" className="font-mono text-[11.5px] text-faint transition hover:text-dim">
        ← tournaments
      </Link>

      <header className="chamfer relative mt-3 overflow-hidden bg-surface">
        <div className="absolute inset-0">
          {art && (
            <img src={art} alt="" className="h-full w-full object-cover opacity-30 saturate-[0.8]" />
          )}
          <div
            className="absolute inset-0"
            style={{ background: `linear-gradient(100deg, #101016f2 30%, ${color}26 130%)` }}
          />
        </div>
        <div className="relative flex flex-wrap items-end justify-between gap-4 px-6 py-8">
          <div className="min-w-0">
            <div className="flex items-center gap-2.5">
              <span className="h-2 w-2 rounded-full" style={{ background: color }} />
              <span className="label-xs">{group.display?.schedule_info || "tournament"}</span>
            </div>
            <h1 className="display mt-1.5 text-2xl leading-tight text-fg sm:text-3xl">
              {group.title}
            </h1>
            <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 font-mono text-[11px] uppercase tracking-wider text-faint">
              {pills.map((p, i) => (
                <Fragment key={p}>
                  {i > 0 && <span className="text-border-strong">·</span>}
                  <span>{p}</span>
                </Fragment>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {regions.length > 1 && (
              <div
                role="group"
                aria-label="Region"
                className="flex items-center gap-0.5 rounded-lg bg-raised/70 p-0.5 font-mono text-[12px]"
              >
                {regions.map(r => (
                  <button
                    key={r}
                    onClick={() => setRegion(r)}
                    aria-pressed={r === active}
                    className={`press rounded-md px-2.5 py-1 font-medium uppercase tracking-wider ${
                      r === active ? "bg-accent/15 text-accent" : "text-faint hover:text-dim"
                    }`}
                  >
                    {r}
                  </button>
                ))}
              </div>
            )}
            {admin && <TrackToggle baseId={baseId} isTracked={isTracked} size="md" />}
          </div>
        </div>
      </header>

      {windows.length === 0 ? (
        <div className="mt-10">
          <p className="text-[13px] text-faint">No windows announced for {active} yet.</p>
          {otherRegion && (
            <button
              onClick={() => setRegion(otherRegion)}
              className="press mt-2 font-mono text-[12px] text-accent transition hover:text-accent-bright"
            >
              Try {otherRegion} →
            </button>
          )}
        </div>
      ) : (
        <>
          {schedule.verdict && schedule.verdictKind && (
            <NowVerdict
              key={`${active}:${schedule.verdict.w.event_window_id}`}
              a={schedule.verdict}
              kind={schedule.verdictKind}
              eventId={eventId}
              liveCount={schedule.liveCount}
              color={color}
              now={now}
            />
          )}
          {windows.length > 1 && (
            <ScheduleRail
              key={active}
              schedule={schedule}
              eventId={eventId}
              region={active}
              storedByWindow={storedByWindow}
              watchedById={watchedById}
              admin={admin}
              now={now}
            />
          )}
        </>
      )}
    </main>
  );
}

function RTag({ round, className = "" }: { round: number; className?: string }) {
  if (round <= 0) return null;
  return (
    <span
      className={`chamfer-sm shrink-0 bg-accent/10 px-1.5 py-0.5 font-mono text-[10px] leading-none text-accent ${className}`}
    >
      R{round}
    </span>
  );
}

/** Pure ticking text — fed `now` from above, never owns an interval. */
function Countdown({ targetMs, now, className }: { targetMs: number; now: number; className?: string }) {
  return <span className={className}>{countdown(targetMs - now)}</span>;
}

/** The answer. One glowing, dominant card: which window, when, and the biggest
 *  press target on the page. live-glow is reserved for genuine live only. */
function NowVerdict({
  a,
  kind,
  eventId,
  liveCount,
  color,
  now,
}: {
  a: Annotated;
  kind: Verdict;
  eventId: string;
  liveCount: number;
  color: string;
  now: number;
}) {
  const w = a.w;
  const href = `/window/${encodeURIComponent(w.event_window_id)}`;
  const label = windowLabel(w.event_window_id, eventId);

  const verb = kind === "live" ? "Watching now" : kind === "recent" ? "Most recent" : "Up next";
  const verbCls = kind === "live" ? "text-ok" : kind === "recent" ? "text-faint" : "text-accent";
  const dotCls =
    kind === "live" ? "bg-ok pulse-dot" : kind === "recent" ? "bg-faint" : "bg-accent";
  const cta = kind === "live" ? "Watch live" : kind === "recent" ? "View results" : "Open";
  const imminent = kind === "next" && a.begin - now < IMMINENT_MS;
  const clockCls =
    kind === "live" ? "text-ok" : imminent ? "text-warn" : "text-accent-bright";

  return (
    <Link to={href} className="group mt-6 block">
      <div
        className={`fade-up press-card chamfer relative overflow-hidden bg-surface ${
          kind === "live" ? "live-glow" : ""
        }`}
      >
        <div
          className="pointer-events-none absolute inset-0"
          style={{ background: `radial-gradient(125% 145% at 100% 0%, ${color}24, transparent 58%)` }}
        />
        <div className="relative flex flex-col gap-5 px-5 py-6 sm:flex-row sm:items-stretch sm:gap-0 sm:px-7 sm:py-7">
          <div className="flex shrink-0 items-center sm:w-[156px]">
            <span
              className={`flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.18em] ${verbCls}`}
            >
              <span className={`h-1.5 w-1.5 rounded-full ${dotCls}`} />
              {verb}
            </span>
          </div>

          <div className="min-w-0 flex-1 sm:border-l sm:border-border/70 sm:pl-6">
            <div className="label-xs">window</div>
            <div className="mt-1 flex items-start gap-2.5">
              <h2 className="display line-clamp-2 text-2xl leading-[1.05] text-fg sm:text-[28px]">
                {label}
              </h2>
              <RTag round={w.round} className="mt-1" />
            </div>
            <div className="mt-2 font-mono text-[13px] text-dim">{shortDate(w.begin_time)}</div>
            {liveCount > 1 && (
              <div className="mt-2 font-mono text-[11px] text-ok">+{liveCount - 1} more live →</div>
            )}
          </div>

          <div className="flex items-center justify-between gap-4 sm:w-[184px] sm:flex-col sm:items-end sm:justify-between sm:pl-6">
            <div className="text-left sm:text-right">
              <div className="label-xs">
                {kind === "live" ? "ends in" : kind === "recent" ? "played" : "starts in"}
              </div>
              {kind === "recent" ? (
                <span className="font-mono text-[20px] font-semibold leading-none text-faint">
                  {relativeTime(w.end_time)}
                </span>
              ) : (
                <Countdown
                  targetMs={kind === "live" ? a.end : a.begin}
                  now={now}
                  className={`font-mono text-[26px] font-semibold leading-none tabular-nums transition-colors duration-300 ${clockCls}`}
                />
              )}
            </div>
            <span
              className={`chamfer-sm inline-flex items-center gap-1.5 px-5 py-2.5 font-mono text-[13px] font-medium uppercase tracking-wider transition-colors ${
                kind === "live"
                  ? "bg-accent/20 text-accent-bright group-hover:bg-accent/30"
                  : kind === "recent"
                    ? "bg-raised text-dim group-hover:bg-overlay group-hover:text-fg"
                    : "bg-accent/15 text-accent group-hover:bg-accent/25"
              }`}
            >
              {cta} <span className="transition-transform group-hover:translate-x-0.5">→</span>
            </span>
          </div>
        </div>
      </div>
    </Link>
  );
}

/** The single horizontal "you are here" rule, drawn once between the last
 *  played and first upcoming window of the focused day. */
function NowMarker({ now }: { now: number }) {
  const { time, suffix } = clockParts(now);
  return (
    <li className="relative flex items-center gap-2.5 px-4 py-2">
      <span className="chamfer-sm inline-flex shrink-0 items-center gap-1.5 bg-accent/15 px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider text-accent">
        <span className="pulse-dot h-1.5 w-1.5 rounded-full bg-accent" />
        Now · {time} {suffix}
      </span>
      <span className="now-draw h-px flex-1 bg-accent/40" />
    </li>
  );
}

function ScheduleRail({
  schedule,
  eventId,
  region,
  storedByWindow,
  watchedById,
  admin,
  now,
}: {
  schedule: Schedule;
  eventId: string;
  region: string;
  storedByWindow: Map<string, number>;
  watchedById: Map<string, WindowInfo>;
  admin: boolean;
  now: number;
}) {
  const [open, setOpen] = useState(false);
  const { bands, focusedKey, verdict, verdictKind, nextUpId } = schedule;
  const verdictId = verdict?.w.event_window_id ?? null;
  const total = bands.reduce((n, b) => n + b.windows.length, 0);

  // Collapse fully-played days that are older than the most recent one — but
  // never the most-recent played day, and only once the list is long enough to
  // earn the disclosure. A just-played window stays one click away, expanded.
  const pastIdxs = bands.map((b, i) => (b.allPast ? i : -1)).filter(i => i >= 0);
  const mostRecentPast = pastIdxs.length ? pastIdxs[pastIdxs.length - 1]! : -1;
  const collapsible = total > 7 && pastIdxs.length > 1;
  // Never collapse the most-recent played day, nor the focused (verdict) day —
  // the hero's "shown above ↑" anchor must always remain visible (the verdict
  // can be an earlier-beginning but later-ending window than mostRecentPast).
  const collapsedSet = new Set(
    collapsible ? pastIdxs.filter(i => i !== mostRecentPast && bands[i]!.key !== focusedKey) : [],
  );
  const collapsedCount = bands
    .filter((_, i) => collapsedSet.has(i))
    .reduce((n, b) => n + b.windows.length, 0);

  const renderBand = (band: Band) => (
    <DayBand
      key={band.key}
      band={band}
      eventId={eventId}
      verdictId={verdictId}
      verdictKind={verdictKind}
      nextUpId={nextUpId}
      isFocused={band.key === focusedKey}
      storedByWindow={storedByWindow}
      watchedById={watchedById}
      admin={admin}
      now={now}
    />
  );

  return (
    <section className="mt-10">
      <SectionHeader title={`Full schedule · ${region}`}>
        <span className="font-mono text-[11px] text-faint">
          {total} {total === 1 ? "window" : "windows"}
        </span>
      </SectionHeader>
      <div className="space-y-5">
        {collapsedSet.size > 0 && (
          <div>
            <button
              onClick={() => setOpen(o => !o)}
              aria-expanded={open}
              aria-controls={`earlier-${region}`}
              className="press group flex w-full items-center gap-2 py-1 font-mono text-[12px] text-faint transition hover:text-dim"
            >
              <svg
                viewBox="0 0 12 12"
                className={`h-3 w-3 transition-transform ${open ? "rotate-90" : ""}`}
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
              >
                <path d="M4.5 2.5 8 6l-3.5 3.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              Earlier · {collapsedCount} {collapsedCount === 1 ? "window" : "windows"} played
            </button>
            {open && (
              <div id={`earlier-${region}`} className="mt-4 space-y-5">
                {bands.filter((_, i) => collapsedSet.has(i)).map(renderBand)}
              </div>
            )}
          </div>
        )}
        {bands.filter((_, i) => !collapsedSet.has(i)).map(renderBand)}
      </div>
    </section>
  );
}

function DayBand({
  band,
  eventId,
  verdictId,
  verdictKind,
  nextUpId,
  isFocused,
  storedByWindow,
  watchedById,
  admin,
  now,
}: {
  band: Band;
  eventId: string;
  verdictId: string | null;
  verdictKind: Verdict | null;
  nextUpId: string | null;
  isFocused: boolean;
  storedByWindow: Map<string, number>;
  watchedById: Map<string, WindowInfo>;
  admin: boolean;
  now: number;
}) {
  const firstValid = band.windows.find(a => a.valid);
  const headerDate = firstValid
    ? new Date(firstValid.begin).toLocaleDateString(undefined, { month: "short", day: "numeric" })
    : "";
  const focusTint = isFocused && (verdictKind === "live" || verdictKind === "next");

  // The standalone NOW rule belongs to TODAY's band, at the boundary between
  // played and not-yet-played windows — never a far-future day (that would read
  // as "you are here" somewhere you are not). Suppressed once the event is over
  // (recent), or when a live row in this band already anchors "now".
  const showMarker = band.key === new Date(now).toDateString() && verdictKind !== "recent" && !band.hasLive;
  const markerIdx = showMarker ? band.windows.findIndex(a => a.state !== "past") : -1;
  const markerAtEnd = showMarker && markerIdx === -1;

  return (
    <div
      className={`fade-up chamfer overflow-hidden bg-surface ${
        focusTint ? "ring-1 ring-inset ring-ok/15" : ""
      }`}
    >
      <div
        className={`flex items-center justify-between gap-3 border-b border-border/60 px-4 py-2.5 ${
          focusTint ? "bg-ok/[0.04]" : ""
        }`}
      >
        <div className="flex items-baseline gap-2">
          <span className={`display text-[13px] ${focusTint ? "text-ok" : "text-fg"}`}>
            {dayLabel(band.key, now)}
          </span>
          {headerDate && <span className="font-mono text-[11px] text-dim">{headerDate}</span>}
        </div>
        <span className="font-mono text-[11px] text-faint">
          {band.windows.length} {band.windows.length === 1 ? "window" : "windows"}
        </span>
      </div>
      <ol className="divide-y divide-border/50">
        {band.windows.map((a, i) => (
          <Fragment key={a.w.event_window_id}>
            {i === markerIdx && <NowMarker now={now} />}
            <ScheduleRow
              a={a}
              state={rowStateOf(a, nextUpId, now)}
              eventId={eventId}
              isVerdict={a.w.event_window_id === verdictId}
              stored={storedByWindow.get(a.w.event_window_id) ?? 0}
              watched={watchedById.get(a.w.event_window_id)}
              admin={admin}
              now={now}
            />
          </Fragment>
        ))}
        {markerAtEnd && <NowMarker now={now} />}
      </ol>
    </div>
  );
}

function ScheduleRow({
  a,
  state,
  eventId,
  isVerdict,
  stored,
  watched,
  admin,
  now,
}: {
  a: Annotated;
  state: RowState;
  eventId: string;
  isVerdict: boolean;
  stored: number;
  watched: WindowInfo | undefined;
  admin: boolean;
  now: number;
}) {
  const { refreshStatus } = useApp();
  const [busy, setBusy] = useState<string | null>(null);
  const w = a.w;
  const href = `/window/${encodeURIComponent(w.event_window_id)}`;
  const label = windowLabel(w.event_window_id, eventId);
  const { time, suffix } = clockParts(w.begin_time);

  const loud = state === "live" || state === "next";
  const clockCls = loud ? "text-fg" : state === "upcoming" ? "text-dim" : "text-faint";
  const labelCls = loud ? "text-fg" : state === "upcoming" ? "text-dim" : "text-faint";
  const imminent = state === "next" && a.begin - now < IMMINENT_MS;

  // Under-clock relative time: the single next-up row is the ONLY accent-bright
  // one; far-future ("later") rows suppress it so distance is felt, not read.
  let rel: { txt: string; cls: string } | null = null;
  if (state === "next") rel = { txt: relativeTime(w.begin_time), cls: "text-accent-bright" };
  else if (state === "upcoming") rel = { txt: relativeTime(w.begin_time), cls: "text-dim" };
  else if (state === "past" && !Number.isNaN(a.end))
    rel = { txt: relativeTime(w.end_time), cls: "text-dim" };

  const subline =
    admin && watched
      ? `${watched.watching} parsing · ${watched.done} captured · ${watched.discovered} queued`
      : state === "past"
        ? stored > 0
          ? `${stored} ${stored === 1 ? "lobby" : "lobbies"} captured`
          : "played"
        : null;

  const run = async (kind: "watch" | "unwatch" | "backfill") => {
    if (busy) return;
    setBusy(kind);
    try {
      if (kind === "watch") await api.watchWindow(w.event_window_id, eventId);
      if (kind === "unwatch") await api.unwatchWindow(w.event_window_id);
      if (kind === "backfill") await api.backfillWindow(w.event_window_id, eventId);
      await refreshStatus();
    } catch {
      // nav status chip carries the offline story
    } finally {
      setBusy(null);
    }
  };

  const adminButtons =
    admin &&
    (watched ? (
      <button
        onClick={() => run("unwatch")}
        disabled={busy != null}
        className="press chamfer-sm bg-accent/15 px-2.5 py-1 font-mono text-[11px] font-medium uppercase tracking-wider text-accent transition-colors hover:bg-accent/25"
      >
        {busy === "unwatch" ? "…" : "watching ✓"}
      </button>
    ) : state === "past" ? (
      <button
        onClick={() => run("backfill")}
        disabled={busy != null}
        title="parse every match of this window (historical testing)"
        className="press chamfer-sm bg-raised px-2.5 py-1 font-mono text-[11px] font-medium uppercase tracking-wider text-dim transition-colors hover:bg-overlay hover:text-fg"
      >
        {busy === "backfill" ? "…" : "backfill"}
      </button>
    ) : (
      <button
        onClick={() => run("watch")}
        disabled={busy != null}
        className="press chamfer-sm bg-raised px-2.5 py-1 font-mono text-[11px] font-medium uppercase tracking-wider text-dim transition-colors hover:bg-overlay hover:text-fg"
      >
        {busy === "watch" ? "…" : "watch"}
      </button>
    ));

  return (
    <li
      className={`group/row flex items-center gap-x-3 px-4 py-3 transition-colors ${
        isVerdict
          ? state === "live"
            ? "bg-ok/[0.06]"
            : "bg-accent/[0.05]"
          : state === "past"
            ? "hover:bg-raised/25"
            : "hover:bg-raised/40"
      }`}
    >
      <Link to={href} className="press-card flex min-w-0 flex-1 items-center gap-x-3">
        <div className="w-[58px] shrink-0">
          <div className="flex items-baseline gap-0.5">
            <span className={`font-mono text-[17px] leading-none tabular-nums ${clockCls}`}>
              {time}
            </span>
            {suffix && <span className="font-mono text-[9.5px] text-dim">{suffix}</span>}
          </div>
          {rel && <div className={`mt-1 font-mono text-[10px] leading-none ${rel.cls}`}>{rel.txt}</div>}
        </div>

        <RTag round={w.round} />

        <div className="min-w-0 flex-1">
          <div className={`truncate text-[13.5px] ${labelCls} ${state === "next" ? "font-medium" : ""}`}>
            {label}
          </div>
          {subline && (
            <div className="mt-0.5 truncate font-mono text-[10.5px] text-dim">{subline}</div>
          )}
        </div>
      </Link>

      <div className="flex shrink-0 items-center gap-1.5">
        {!isVerdict && state === "live" && <LiveBadge />}
        {!isVerdict && state === "next" && imminent && (
          <Countdown
            targetMs={a.begin}
            now={now}
            className="chamfer-sm bg-warn/10 px-2 py-0.5 font-mono text-[11px] tabular-nums text-warn"
          />
        )}
        {adminButtons}
        {isVerdict ? (
          <span className="font-mono text-[10.5px] text-faint">shown above ↑</span>
        ) : (
          <Link
            to={href}
            className={`press chamfer-sm px-2.5 py-1 font-mono text-[11px] font-medium uppercase tracking-wider text-accent transition-colors ${
              loud ? "bg-accent/15 hover:bg-accent/25" : "bg-accent/10 hover:bg-accent/20"
            }`}
          >
            open →
          </Link>
        )}
      </div>
    </li>
  );
}
